import React, { useEffect, useState } from 'react';
import { View, Modal } from 'react-native';
import Toast from 'react-native-toast-message';
import FormActions from '../../../components/FormActions';
import FormInput from '../../../components/FormInput';
import { ClientError } from '../../../services/central-api/base-service';
import { dispatchService } from '../../../services/central-api/dispatch';
import { WindowInformation } from '../../../services/window-information';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../OccurrenceTypes/ActingBodiesDropdown';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';

interface Props {
    isModalVisible: boolean;
    setModalVisible: (v: boolean) => void;
    unitDisplacedReasonId?: number;
    onClose: () => void;
    authenticatedUser?: AuthenticatedUser;
}

export default function UnitDisplacedReasonModal({
    isModalVisible,
    setModalVisible,
    unitDisplacedReasonId,
    onClose,
    authenticatedUser
}: Props) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [category, setCategory] = useState<string>('');

    const policeStationOptions = [
        { value: '', label: '' },
        { value: 'arrested', label: translate('delegacy') },
        { value: 'hospitalized', label: translate('hospital') },
    ];

    function clearAll() {
        setName('');
        setActingBodyId(0);
        setCategory('');
    }

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    async function loadReason(id: number) {
        try {
            const reason = await dispatchService.getUnitDisplacedReasonById(id);
            setName(reason.name);
            setActingBodyId(reason.actingBody?.id ?? 0);
            setCategory(reason.category ?? '');
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (isModalVisible && unitDisplacedReasonId) {
            loadReason(unitDisplacedReasonId);
        } else {
            clearAll();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        try {
            const payload = {
                name: name.trim(),
                actingBodyId: actingBodyId || undefined,
                category: category ? category : undefined,
            };
            if (unitDisplacedReasonId) {
                await dispatchService.updateUnitDisplacedReason(unitDisplacedReasonId, payload);
            } else {
                await dispatchService.createUnitDisplacedReason(payload);
            }
            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            onClose();
            setModalVisible(false);
            clearAll();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            }
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        }
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType="fade"
            onRequestClose={() => {
                onClose();
                clearAll();
            }}
        >
            <View style={styles.container}>
                <View
                    style={[
                        styles.formContainer,
                        windowInfo.isMobile ? { width: '95%' } : { width: '30%' }
                    ]}
                >
                    {(authenticatedUser?.isAdmin ||
                        authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_displaced_reasons) &&
                        <ActingBodiesDropdown
                            includeAllOption={false}
                            value={actingBodyId}
                            setValue={setActingBodyId}
                            zIndex={2}
                            disabled={!!unitDisplacedReasonId}
                        />
                    }
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={name}
                        onChangeText={setName}
                        invalid={isNameInvalid}
                    />

                    <FilterDropdown
                        label={translate('type')}
                        items={policeStationOptions}
                        multiple={false}
                        value={category}
                        setValue={setCategory}
                        zIndex={1}
                    />
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            onClose();
                            clearAll();
                        }}
                        disabled={!isFormValid()}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        backgroundColor: '#31313199',
        justifyContent: 'center',
        alignItems: 'center',
    },
    formContainer: {
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25,
    },
}));
